import React, { useState, useRef, useEffect } from 'react';
import '../CSS/NewsTesting.css';

interface ContentBlock {
  type: string;
  content: string;
  attributes?: Record<string, any>;
}

interface NewsContent {
  title?: string;
  blocks: ContentBlock[];
  metadata?: {
    createdAt?: string;
    updatedAt?: string;
    author?: string;
  };
}

const NewsTesting: React.FC = () => {
  const [content, setContent] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [exportData, setExportData] = useState<NewsContent | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  // Formatting functions
  const formatText = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
  };

  // Update content when editor changes
  const handleInput = () => {
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
  };

  // Convert HTML content to structured JSON blocks
  const convertToJSON = (): NewsContent => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const body = doc.body;
    const blocks: ContentBlock[] = [];

    // Process each child node
    Array.from(body.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
          blocks.push({
            type: 'paragraph',
            content: text,
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();

        switch (tagName) {
          case 'h1':
          case 'h2':
          case 'h3':
          case 'h4':
          case 'h5':
          case 'h6':
            blocks.push({
              type: 'heading',
              content: element.textContent || '',
              attributes: { level: parseInt(tagName.charAt(1)) },
            });
            break;
          case 'p':
            blocks.push({
              type: 'paragraph',
              content: element.innerHTML,
            });
            break;
          case 'ul':
          case 'ol':
            const items: string[] = [];
            element.querySelectorAll('li').forEach((li) => {
              items.push(li.textContent || '');
            });
            blocks.push({
              type: 'list',
              content: JSON.stringify(items),
              attributes: { listType: tagName === 'ul' ? 'unordered' : 'ordered' },
            });
            break;
          case 'blockquote':
            blocks.push({
              type: 'quote',
              content: element.textContent || '',
            });
            break;
          case 'a':
            blocks.push({
              type: 'link',
              content: element.textContent || '',
              attributes: { href: element.getAttribute('href') || '' },
            });
            break;
          case 'img':
            blocks.push({
              type: 'image',
              content: element.getAttribute('src') || '',
              attributes: {
                alt: element.getAttribute('alt') || '',
                width: element.getAttribute('width') || '',
                height: element.getAttribute('height') || '',
              },
            });
            break;
          default:
            if (element.textContent?.trim()) {
              blocks.push({
                type: 'paragraph',
                content: element.innerHTML,
              });
            }
        }
      }
    });

    // If no blocks were created but there's content, create a paragraph block
    if (blocks.length === 0 && content.trim()) {
      blocks.push({
        type: 'paragraph',
        content: content,
      });
    }

    return {
      title: title || undefined,
      blocks,
      metadata: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  };

  // Export content as JSON
  const handleExport = () => {
    const jsonData = convertToJSON();
    setExportData(jsonData);
    
    // Create downloadable JSON file
    const dataStr = JSON.stringify(jsonData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `news-content-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Copy JSON to clipboard
  const handleCopyJSON = () => {
    const jsonData = convertToJSON();
    const jsonString = JSON.stringify(jsonData, null, 2);
    navigator.clipboard.writeText(jsonString).then(() => {
      alert('JSON copied to clipboard!');
    });
  };

  // Clear editor
  const handleClear = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
      setContent('');
      setTitle('');
      setExportData(null);
    }
  };

  // Insert image
  const handleInsertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      formatText('insertImage', url);
    }
  };

  // Insert link
  const handleInsertLink = () => {
    const url = prompt('Enter link URL:');
    if (url) {
      formatText('createLink', url);
    }
  };

  return (
    <div className="news-testing-container">
      <div className="news-testing-header">
        <h1>News Content Editor</h1>
        <p>Create and edit content with WYSIWYG tools. Export as JSON for backend integration.</p>
      </div>

      <div className="news-testing-editor-wrapper">
        {/* Title Input */}
        <div className="news-testing-title-section">
          <label htmlFor="news-title">Title:</label>
          <input
            id="news-title"
            type="text"
            className="news-testing-title-input"
            placeholder="Enter article title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* Toolbar */}
        <div className="news-testing-toolbar">
          <div className="news-testing-toolbar-group">
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('bold')}
              title="Bold"
            >
              <strong>B</strong>
            </button>
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('italic')}
              title="Italic"
            >
              <em>I</em>
            </button>
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('underline')}
              title="Underline"
            >
              <u>U</u>
            </button>
          </div>

          <div className="news-testing-toolbar-group">
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('formatBlock', 'h1')}
              title="Heading 1"
            >
              H1
            </button>
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('formatBlock', 'h2')}
              title="Heading 2"
            >
              H2
            </button>
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('formatBlock', 'h3')}
              title="Heading 3"
            >
              H3
            </button>
          </div>

          <div className="news-testing-toolbar-group">
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('formatBlock', 'p')}
              title="Paragraph"
            >
              P
            </button>
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('insertUnorderedList')}
              title="Bullet List"
            >
              • List
            </button>
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('insertOrderedList')}
              title="Numbered List"
            >
              1. List
            </button>
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('formatBlock', 'blockquote')}
              title="Quote"
            >
              "
            </button>
          </div>

          <div className="news-testing-toolbar-group">
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={handleInsertLink}
              title="Insert Link"
            >
              🔗 Link
            </button>
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={handleInsertImage}
              title="Insert Image"
            >
              🖼️ Image
            </button>
          </div>

          <div className="news-testing-toolbar-group">
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('justifyLeft')}
              title="Align Left"
            >
              ⬅
            </button>
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('justifyCenter')}
              title="Align Center"
            >
              ⬌
            </button>
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('justifyRight')}
              title="Align Right"
            >
              ➡
            </button>
          </div>

          <div className="news-testing-toolbar-group">
            <button
              type="button"
              className="news-testing-toolbar-btn"
              onClick={() => formatText('removeFormat')}
              title="Remove Formatting"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Editor */}
        <div
          ref={editorRef}
          className="news-testing-editor"
          contentEditable
          onInput={handleInput}
          suppressContentEditableWarning
          data-placeholder="Start typing your content here..."
        />

        {/* Action Buttons */}
        <div className="news-testing-actions">
          <button
            type="button"
            className="news-testing-action-btn news-testing-action-btn-primary"
            onClick={handleExport}
          >
            Export as JSON
          </button>
          <button
            type="button"
            className="news-testing-action-btn news-testing-action-btn-secondary"
            onClick={handleCopyJSON}
          >
            Copy JSON to Clipboard
          </button>
          <button
            type="button"
            className="news-testing-action-btn news-testing-action-btn-danger"
            onClick={handleClear}
          >
            Clear Editor
          </button>
        </div>

        {/* JSON Preview */}
        {exportData && (
          <div className="news-testing-preview">
            <h3>JSON Preview:</h3>
            <pre className="news-testing-json-preview">
              {JSON.stringify(exportData, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsTesting;

